import React, { Component } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import * as R from 'ramda'

import Select from '../../components/form/Select'
import Input from '../../components/form/Input'
import TextArea from '../../components/form/TextArea'
import Checkbox from '../../components/form/Checkbox'
import Date from '../../components/form/Date'
import CheckboxGroup from '../../components/form/CheckboxGroup'
import QuestionPicker from '../components/QuestionPicker'
import NPSAnswerOptions from '../components/NPSAnswerOptions'
import CKeditor5 from '../../components/CKeditor5'
import OptionEditor from '../components/OptionEditor'
import ProductTypes from '../components/ProductTypes'
import HeaderGroups from '../components/HeaderGroup'
import InfoAccordion from '../../components/InfoAccordion'
import Translate from '../../components/Translate'
import HoverInfo from '../../UniformComponents/HoverInfo'

@observer
class QuestionForm extends Component {
    constructor(props) {
        super(props)
        this.state = {
            mandatoryFields: [],
        }
    }

    register = (name, validate) => {
        const { store: { regFields }, formFields } = this.props
        regFields[name] = validate
        const mandatoryFields = formFields
            .filter((field) => (field.isMandatory))
            .map((field) => (field.name))
        this.setState({ mandatoryFields })
    }

    unregister = (name) => {
        const { store: { regFields } } = this.props
        const { mandatoryFields } = this.state
        if (!mandatoryFields.includes(name)) Reflect.deleteProperty(regFields, name)
    }

    getHistoricalDataInfoDetail = (field) => {
        const { store } = this.props
        if (field.oid <= 5) {
            return <p dangerouslySetInnerHTML={{ __html: i18n.t('questionLibrary.infoAccordion.editDisabled.linked') }} />
        }
        return (
            <>
                {store.listOfOtherSurveys.length > 0
                    ? (
                        <>
                            <p dangerouslySetInnerHTML={{ __html: i18n.t('questionBuilder.infoAccordion.editDisabled.linked.1') }} />
                            <ul>
                                {store.listOfOtherSurveys.map(({ name }) => (
                                    <li>{name}</li>
                                ))}
                            </ul>
                            <br />
                        </>
                    )
                    : null}
                <p dangerouslySetInnerHTML={{ __html: i18n.t('questionBuilder.infoAccordion.editDisabled.linked.2') }} />
            </>
        )
    }

    renderFields = (store, formFields) => {
        const { tabName } = store

        return (
            formFields.map((field) => {
                if (field && !field.show) return null
                switch (field.type) {
                    case 'translate': {
                        return (
                            <Translate
                                store={store}
                                question={store.question}
                                locales={store.availableLocales}
                                defaultLocale={store.defaultLocale}
                                isDisabled={field.hasLiveResponse}
                                hasExistingData={field.hasLiveResponse}
                            />
                        )
                    }
                    case 'npsAnswerOptions': {
                        return <NPSAnswerOptions key={field.compName} />
                    }
                    case 'questionPicker': {
                        return (
                            <QuestionPicker
                                key={field.name}
                                options={field.options}
                                value={field.value}
                                onChange={field.onChange}
                                disabled={field.disabled}
                                showAsDropdown={tabName !== 'newQuestion'}
                            />
                        )
                    }
                    case 'historicalData': {
                        return field.isNew() || (field.oid > 5 && !field.hasLiveResponse)
                            ? null
                            : (
                                <InfoAccordion
                                    title={field.oid <= 5
                                        ? i18n.t('q_historical_data_title')
                                        : i18n.t('q_historical_data_title_product')}
                                    show={store.accState.editQuestionDisabled}
                                    toggle={() => store.toggleAccordion('editQuestionDisabled')}
                                    cssClass="warning"
                                >
                                    {this.getHistoricalDataInfoDetail(field)}
                                </InfoAccordion>
                            )
                    }
                    case 'select': {
                        return (
                            <Select
                                key={field.name}
                                register={this.register}
                                unregister={this.unregister}
                                name={field.name}
                                label={field.label}
                                options={field.options}
                                defaultValue={field.value}
                                value={typeof field.value === 'object' ? field.value
                                    : field.options.find(
                                        (res) => (res.value === field.value),
                                    )}
                                onBlur={async (e) => {
                                    if (R.hasPath(['onBlur'], field)) await field.onBlur(e)
                                    else store.setField(e, 'common')
                                    store.setValueHolder(e)
                                }}
                                placeholder={field.placeholder || i18n.t('form.select.placeholder')}
                                isMandatory={field.isMandatory}
                                emptyError={`${field.label} ${i18n.t('required')}`}
                                cssClass={field.cssClass}
                                showDisabled={field.disabled}
                                isMulti={field.isMulti}
                                blurOnClear
                                validate={field.validate}
                                isSearchable={field.isSearchable}
                                isClearable={field.isClearable}
                                tooltipText={field.tooltipText}
                                tooltipTitle={field.tooltipTitle}
                                tooltipLinkLabel={field.tooltipLinkLabel}
                                tooltipLink={field.tooltipLink}
                            />
                        )
                    }
                    case 'input': {
                        return (
                            <Input
                                key={`${field.name}_${store.selectedLocale}`}
                                name={field.name}
                                type={field.inputType}
                                register={this.register}
                                unregister={this.unregister}
                                defaultValue={field.defaultValue}
                                value={field.value}
                                onBlur={async (e) => {
                                    if (R.hasPath(['onBlur'], field)) await field.onBlur(e)
                                    else store.setField(e, field.inputType || 'common')
                                    store.setValueHolder(e)
                                }}
                                placeholder={field.placeholder || i18n.t('form.input.placeholder')}
                                label={field.label}
                                cssClass={field.cssClass}
                                isMandatory={field.isMandatory}
                                emptyError={`${field.label} ${i18n.t('required')}`}
                                format={field.format}
                                formatError={field.formatError}
                                min={field.min || 0}
                                max={field.max}
                                disabled={field.disabled}
                                tooltipText={field.tooltipText}
                                tooltipTitle={field.tooltipTitle}
                                tooltipLinkLabel={field.tooltipLinkLabel}
                                tooltipLink={field.tooltipLink}
                                warningMsg={field.warningMsg}
                                onEdit={field.onEdit}
                            />
                        )
                    }
                    case 'textarea': {
                        return (
                            <TextArea
                                key={field.name}
                                register={this.register}
                                unregister={this.unregister}
                                defaultValue={field.defaultValue}
                                onBlur={async (e) => {
                                    if (R.hasPath(['onBlur'], field)) await field.onBlur(e)
                                    else store.setField(e, 'common')
                                    store.setValueHolder(e)
                                }}
                                placeholder={field.placeholder || i18n.t('form.input.placeholder')}
                                label={field.label}
                                cssClass={field.cssClass}
                                textAreaCssStyle={field.textAreaCssStyle}
                                name={field.name}
                                isMandatory={field.isMandatory}
                                emptyError={`${field.label} ${i18n.t('required')}`}
                                rows={field.rows}
                                cols={field.cols}
                                tooltipText={field.tooltipText}
                                tooltipTitle={field.tooltipTitle}
                                tooltipLinkLabel={field.tooltipLinkLabel}
                                tooltipLink={field.tooltipLink}
                            />
                        )
                    }
                    case 'checkbox': {
                        return (
                            <Checkbox
                                key={field.name}
                                register={this.register}
                                unregister={this.unregister}
                                onBlur={async (e) => {
                                    if (R.hasPath(['onBlur'], field)) await field.onBlur(e)
                                    else store.setField(e, 'common')
                                    store.setValueHolder(e)
                                }}
                                label={field.label}
                                labelDir={field.labelDir}
                                cssClass={field.cssClass}
                                name={field.name}
                                defaultChecked={field.defaultChecked}
                                checked={field.checked}
                                disabled={field.disabled}
                                stopPropagation
                            />
                        )
                    }
                    case 'checkbox-group': {
                        return (
                            <CheckboxGroup
                                key={field.name}
                                register={this.register}
                                unregister={this.unregister}
                                onBlur={async (e) => {
                                    if (R.hasPath(['onBlur'], field)) await field.onBlur(e)
                                    else store.setField(e, 'common')
                                    store.setValueHolder(e)
                                }}
                                label={field.label}
                                cssClass={field.cssClass}
                                options={field.options}
                                name={field.name}
                                defaultChecked={field.value}
                                isMandatory={field.isMandatory}
                                returnValueUnchanged
                            />
                        )
                    }
                    case 'checkbox-reactive': {
                        return (
                            <div
                                key={field.name}
                                className={`check-grp ${field.cssClass || ''}`}
                            >
                                <label className="check-grp-label">
                                    {field.label}
                                </label>
                                <div className="check-grp-wrapper">
                                    {this.renderFields(store, field.fields)}
                                </div>
                            </div>
                        )
                    }
                    case '2-cols': {
                        return (
                            <div key={field.name} id="question-title-and-type">
                                {this.renderFields(store, field.fields)}
                            </div>
                        )
                    }
                    case 'date': {
                        return (
                            <Date
                                key={field.name}
                                register={this.register}
                                unregister={this.unregister}
                                name={field.name}
                                label={field.label}
                                placeholder={field.placeholder || i18n.t('form.date.placeholder')}
                                path={field.path}
                                isMandatory={field.isMandatory}
                                value={field.value}
                                minDate={field.minDate}
                                maxDate={field.maxDate}
                                onBlur={async (e) => {
                                    if (R.hasPath(['onBlur'], field)) await field.onBlur(e)
                                    else store.setField(e, 'date')
                                    store.setValueHolder(e)
                                }}
                                emptyError={`${field.label} ${i18n.t('required')}`}
                                tooltipText={field.tooltipText}
                                tooltipTitle={field.tooltipTitle}
                                tooltipLinkLabel={field.tooltipLinkLabel}
                                tooltipLink={field.tooltipLink}
                                allowManualDateEdit={field.allowManualDateEdit}
                            />
                        )
                    }
                    case 'text-editor': {
                        return (
                            <div key={field.name} className={`text-editor-wrapper ${field.disabled ? 'disabled' : ''}`}>
                                <b className="text-editor-label">
                                    {field.label}
                                </b>
                                {!field.disabled ? (
                                    <CKeditor5
                                        store={store}
                                        toolbar={field.toolbar}
                                        onBlur={field.onChange}
                                        placeholder={field.placeholder}
                                        data={field.content}
                                        warningMsg={field.warningMsg}
                                    />
                                ) : (
                                    <div className="content">
                                        <div className="header">
                                            {field.warningMsg && (
                                                <HoverInfo
                                                    cssClass="warning-icon"
                                                    desc={field.warningMsg}
                                                    dir="right"
                                                >
                                                    <i className="fa-regular fa-language warning" />
                                                </HoverInfo>
                                            )}
                                            {field.onEdit && (
                                                <i
                                                    className="edit-icon fa-light fa-pen-to-square"
                                                    onClick={field.onEdit}
                                                />
                                            )}
                                        </div>
                                        <div className="text" dangerouslySetInnerHTML={{__html: field.content }} />
                                    </div>
                                )}
                            </div>
                        )
                    }
                    case 'option-editor': {
                        return (
                            <OptionEditor
                                key={field.defaultValue}
                                name={field.name}
                                optionSet={field.optionSet}
                                selectedLocale={field.selectedLocale}
                                defaultLocale={field.defaultLocale}
                                initialLocale={field.initialLocale}
                                questionTypeCode={field.questionTypeCode}
                                hasLiveResponse={field.hasLiveResponse}
                                inheritedOptions={field.inheritedOptions}
                                isEdit={field.isEdit}
                                contextOid={field.contextOid}
                                changeSorting={field.changeSorting}
                                addOption={field.addOption}
                                createSubQuestionStore={field.createSubQuestionStore}
                                remSubQuestion={field.remSubQuestion}
                                toggleSkipLogic={field.toggleSkipLogic}
                                toggleSubQueModal={field.toggleSubQueModal}
                                toggleSkipLogicAlertModal={field.toggleSkipLogicAlertModal}
                                toggleSubQueMandatory={store.toggleSubQueMandatory}
                                subQuestionMap={field.subQuestionMap}
                                availableLocales={field.availableLocales}
                                clientLanguages={store.appStore.localeOptions}
                                optionMenuComps={field.optionMenuComps}
                                sortByComps={field.sortByComps}
                                buttonComps={field.buttonComps}
                                customFormSubQuestionType={field.customFormSubQuestionType}
                                changeSubQuestionType={field.changeSubQuestionType}
                                optionEditorLoader={field.optionEditorLoader}
                                toggleOptionEditModal={field.toggleOptionEditModal}
                                question={store.question}
                                store={store}
                                onBlur={({ value }) => {
                                    store.setField({ value, name: field.name }, 'common')
                                    store.setValueHolder({ value, name: field.name })
                                }}
                                isExploriClient={field.isExploriClient}
                                isExploriAdmin={field.isExploriAdmin}
                            />
                        )
                    }
                    case 'product-type': {
                        return (
                            <ProductTypes
                                key={field.name}
                                value={field.value}
                                options={field.options}
                                onChange={field.onChange}
                                isAccClose={field.isAccClose}
                                toggleCheckAcc={field.toggleCheckAcc}
                            />
                        )
                    }
                    case 'headerRow':
                        return (
                            <HeaderGroups
                                questionStore={store}
                            />
                        )
                    default:
                        return null
                }
            })
        )
    }

    render() {
        const { store, formFields } = this.props
        return (
            <div className="field-wrapper">
                {this.renderFields(store, formFields)}
            </div>
        )
    }
}

export default QuestionForm
